import { OEmbedError } from "src/types";

import logger from "@/utils/logger";

type OEmbedProvider = {
  name: string;
  scheme: RegExp;
  endpointUrl: string;
};

const providers: OEmbedProvider[] = [
  {
    name: "youtube",
    scheme: /^https:\/\/(((.*).?)youtube\.com\/(watch|v|playlist\?list=|shorts|live)|youtu\.be\/)(.*)/i,
    endpointUrl: "https://www.youtube.com/oembed",
  },
  {
    name: "vimeo",
    scheme: /^https:\/\/(vimeo\.com\/(.*)|player\.vimeo\.com\/video\/(.*))$/i,
    endpointUrl: "https://vimeo.com/api/oembed.json",
  },
  {
    name: "wistia.com",
    scheme: /^https:\/\/(fast\.wistia\.com\/embed\/(playlists|iframe)\/(.*)|(.*\.)wistia\.com\/medias\/(.*))$/i,
    endpointUrl: "https://fast.wistia.com/oembed.json",
  },
  {
    name: "facebook.com",
    scheme: /^https:\/\/www\.facebook\.com\/(video\.php\?(v|id)=|(.*)\/videos\/)(.*)$/i,
    endpointUrl: "https://graph.facebook.com/v16.0/oembed_video",
  },
  {
    name: "streamable.com",
    scheme: /^https:\/\/streamable.com\/(.*)$/i,
    endpointUrl: "https://api.streamable.com/oembed.json",
  },
];

type OEmbedErrorType =
  | "UNSUPPORTED_VIDEO_PROVIDER"
  | "ERROR_FETCHING_VIDEO_DATA";

export type FetchVideoMetadataResponse =
  | {
      error: OEmbedErrorType;
      provider?: string;
    }
  | {
      title: string;
      thumbnail: string;
    };

export const OEmbedErrorTranslationKeys: Record<OEmbedErrorType, string> = {
  UNSUPPORTED_VIDEO_PROVIDER: "unsupported-video-provider",
  ERROR_FETCHING_VIDEO_DATA: "error-fetching-video-data",
};

const getOEmbedProviderUrl = (url: string) => {
  const provider = providers.find((provider) => provider.scheme.test(url));

  if (provider) {
    return provider.endpointUrl;
  }
  return null;
};

export const fetchVideoMetadata = async (
  url: string
): Promise<FetchVideoMetadataResponse> => {
  const oEmbedEndpoint = getOEmbedProviderUrl(url);

  if (!oEmbedEndpoint) {
    logger.error(new OEmbedError("Unsupported provider"), {
      url,
    });
    return {
      error: "UNSUPPORTED_VIDEO_PROVIDER",
    };
  }

  const fullEndpoint = `${oEmbedEndpoint}?url=${encodeURIComponent(url)}`;
  const res = await fetch(fullEndpoint);
  if (!res.ok) {
    logger.error(new OEmbedError("Error in fetching oEmbed data"), {
      endpoint: fullEndpoint,
    });
    return {
      error: "ERROR_FETCHING_VIDEO_DATA",
    };
  }

  const data = await res.json();
  return {
    title: typeof data.title === "string" ? data.title : "",
    thumbnail: typeof data.thumbnail_url === "string" ? data.thumbnail_url : "",
  };
};
